import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Heart, BookOpen, Users, Phone, Calendar, MessageCircle, 
  Flame, Shield, Sun, Stethoscope, Clipboard, Search,
  Play, FileText, Clock, MapPin, Video, ChevronRight,
  AlertCircle, Star, ExternalLink, HandHelping
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categoryIcons = {
  education: BookOpen,
  self_care: Heart,
  family: Users,
  coping: Shield,
  healing: Sun,
  support: HandHelping,
  memorial: Flame,
  clinical: Stethoscope,
  practical: Clipboard
};

const GriefSupport = () => {
  const [activeTab, setActiveTab] = useState('resources');
  const [resources, setResources] = useState([]);
  const [featuredResources, setFeaturedResources] = useState([]);
  const [categories, setCategories] = useState({});
  const [supportGroups, setSupportGroups] = useState([]);
  const [bereavementProgram, setBereavementProgram] = useState(null);
  const [counselingResources, setCounselingResources] = useState(null);
  const [memorialIdeas, setMemorialIdeas] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showSupportRequestDialog, setShowSupportRequestDialog] = useState(false);
  const [showRegistrationDialog, setShowRegistrationDialog] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [supportRequest, setSupportRequest] = useState({
    name: '',
    phone: '',
    email: '',
    support_type: 'counseling',
    preferred_contact: 'phone',
    message: '',
    urgency: 'normal'
  });
  const [registrationData, setRegistrationData] = useState({
    name: '',
    email: '',
    phone: '',
    relationship: '',
    loss_date: '',
    special_needs: ''
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchResources(),
        fetchSupportGroups(),
        fetchBereavementProgram(),
        fetchCounselingResources(),
        fetchMemorialIdeas()
      ]);
    } catch (error) {
      console.error('Error fetching grief support data:', error);
      toast.error('Failed to load grief support resources');
    } finally {
      setLoading(false);
    }
  };

  const fetchResources = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase48/grief/resources`);
      const data = await response.json();
      setResources(data.resources || []);
      setFeaturedResources(data.featured || []);
      setCategories(data.categories || {});
    } catch (error) {
      console.error('Error fetching resources:', error);
    }
  };

  const fetchSupportGroups = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase48/grief/support-groups`);
      const data = await response.json();
      setSupportGroups(data.support_groups || []);
    } catch (error) {
      console.error('Error fetching support groups:', error);
    }
  };

  const fetchBereavementProgram = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase48/grief/bereavement-program`);
      const data = await response.json();
      setBereavementProgram(data);
    } catch (error) {
      console.error('Error fetching bereavement program:', error);
    }
  };

  const fetchCounselingResources = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase48/grief/counseling-resources`);
      const data = await response.json();
      setCounselingResources(data);
    } catch (error) {
      console.error('Error fetching counseling resources:', error);
    }
  };

  const fetchMemorialIdeas = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase48/grief/memorial-ideas`);
      const data = await response.json();
      setMemorialIdeas(data);
    } catch (error) {
      console.error('Error fetching memorial ideas:', error);
    }
  };

  const handleSupportRequest = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase48/grief/request-support`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(supportRequest)
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        setShowSupportRequestDialog(false);
        setSupportRequest({
          name: '',
          phone: '',
          email: '',
          support_type: 'counseling',
          preferred_contact: 'phone',
          message: '',
          urgency: 'normal'
        });
      } else {
        toast.error('Failed to submit support request');
      }
    } catch (error) {
      console.error('Error submitting support request:', error);
      toast.error('Failed to submit support request');
    }
  };

  const handleGroupRegistration = async () => {
    if (!selectedGroup) return;
    try {
      const response = await fetch(`${API_URL}/api/phase48/grief/support-groups/${selectedGroup.id}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(registrationData)
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        setShowRegistrationDialog(false);
        setRegistrationData({
          name: '',
          email: '',
          phone: '',
          relationship: '',
          loss_date: '',
          special_needs: ''
        });
        setSelectedGroup(null);
      } else {
        toast.error('Failed to register for support group');
      }
    } catch (error) {
      console.error('Error registering for support group:', error);
      toast.error('Failed to register for support group');
    }
  };

  const filteredResources = resources.filter(resource => {
    const matchesCategory = selectedCategory === 'all' || resource.category === selectedCategory;
    const matchesSearch = !searchQuery || 
      resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      resource.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading grief support resources...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-teal-700 to-teal-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-4 mb-4">
            <Heart className="h-10 w-10" />
            <h1 className="text-4xl font-bold" data-testid="grief-support-title">Grief & Bereavement Support</h1>
          </div>
          <p className="text-xl text-teal-100 max-w-3xl">
            You are not alone in your grief journey. Our compassionate team is here to support you 
            with resources, counseling, and community connections.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Button 
              size="lg" 
              variant="secondary"
              onClick={() => setShowSupportRequestDialog(true)}
              data-testid="request-support-btn"
            >
              <Phone className="mr-2 h-5 w-5" />
              Request Support
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="bg-white/10 text-white border-white/30 hover:bg-white/20"
              onClick={() => setActiveTab('support-groups')}
            >
              <Users className="mr-2 h-5 w-5" />
              Find a Support Group
            </Button>
          </div>
        </div>
      </div>

      {/* Crisis Banner */}
      <div className="bg-amber-50 border-b border-amber-200 py-3">
        <div className="max-w-7xl mx-auto px-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
          <p className="text-amber-800 text-sm">
            <strong>If you're in crisis:</strong> Call 988 (Suicide & Crisis Lifeline) or Goodwin Cares 24/7 at 703-578-7108
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
          <TabsList className="grid grid-cols-5 gap-2 bg-white p-1 rounded-lg shadow-sm">
            <TabsTrigger value="resources" data-testid="tab-resources">
              <BookOpen className="h-4 w-4 mr-2" />
              Resources
            </TabsTrigger>
            <TabsTrigger value="support-groups" data-testid="tab-support-groups">
              <Users className="h-4 w-4 mr-2" />
              Support Groups
            </TabsTrigger>
            <TabsTrigger value="program" data-testid="tab-program">
              <Calendar className="h-4 w-4 mr-2" />
              Bereavement Program
            </TabsTrigger>
            <TabsTrigger value="counseling" data-testid="tab-counseling">
              <MessageCircle className="h-4 w-4 mr-2" />
              Counseling
            </TabsTrigger>
            <TabsTrigger value="memorials" data-testid="tab-memorials">
              <Flame className="h-4 w-4 mr-2" />
              Memorials
            </TabsTrigger>
          </TabsList>

          {/* Resources Tab */}
          <TabsContent value="resources" className="space-y-6">
            {/* Featured Resources */}
            {featuredResources.length > 0 && (
              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <Star className="h-6 w-6 text-amber-500" />
                  Featured Resources
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {featuredResources.map(resource => (
                    <Card key={resource.id} className="border-l-4 border-l-amber-500 hover:shadow-lg transition-shadow">
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between">
                          <CardTitle className="text-lg">{resource.title}</CardTitle>
                          <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                            {resource.type}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-slate-600 text-sm mb-3">{resource.description}</p>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          {resource.duration && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{resource.duration}</span>}
                          {resource.readTime && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{resource.readTime}</span>}
                          {resource.pages && <span className="flex items-center gap-1"><FileText className="h-3 w-3" />{resource.pages} pages</span>}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Search and Filter */}
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search resources..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="resource-search"
                />
              </div>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full md:w-64" data-testid="category-filter">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {Object.entries(categories).map(([key, cat]) => (
                    <SelectItem key={key} value={key}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Resource Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredResources.map(resource => {
                const IconComponent = categoryIcons[resource.category] || BookOpen;
                return (
                  <Card key={resource.id} className="hover:shadow-lg transition-shadow cursor-pointer" data-testid={`resource-${resource.id}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-teal-50">
                          <IconComponent className="h-5 w-5 text-teal-600" />
                        </div>
                        <div className="flex-1">
                          <CardTitle className="text-base">{resource.title}</CardTitle>
                          <Badge variant="outline" className="mt-1 text-xs">
                            {categories[resource.category]?.name || resource.category}
                          </Badge>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-slate-600 mb-3">{resource.description}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          {resource.type === 'video' && <Play className="h-3 w-3" />}
                          {resource.type === 'guide' && <FileText className="h-3 w-3" />}
                          {resource.type === 'article' && <BookOpen className="h-3 w-3" />}
                          <span className="capitalize">{resource.type}</span>
                          {resource.duration && <span>• {resource.duration}</span>}
                          {resource.readTime && <span>• {resource.readTime}</span>}
                        </div>
                        <ChevronRight className="h-4 w-4 text-slate-400" />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Support Groups Tab */}
          <TabsContent value="support-groups" className="space-y-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-semibold text-slate-800">Grief Support Groups</h2>
              <Badge variant="outline" className="text-teal-600">
                {supportGroups.length} Groups Available
              </Badge>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {supportGroups.map(group => (
                <Card key={group.id} className="hover:shadow-lg transition-shadow" data-testid={`group-${group.id}`}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{group.name}</CardTitle>
                        <CardDescription className="mt-1">{group.description}</CardDescription>
                      </div>
                      <Badge className={group.open_enrollment ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'}>
                        {group.open_enrollment ? 'Open Enrollment' : 'By Registration'}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Calendar className="h-4 w-4" />
                        <span>{group.schedule}</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <MapPin className="h-4 w-4" />
                        <span>{group.location}</span>
                        <Badge variant="outline" className="text-xs">
                          {group.format === 'in_person' ? 'In Person' : group.format === 'virtual' ? 'Virtual' : 'Hybrid'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Users className="h-4 w-4" />
                        <span>{group.current_participants}/{group.max_participants} participants</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Stethoscope className="h-4 w-4" />
                        <span>Facilitator: {group.facilitator}</span>
                      </div>
                    </div>
                    
                    <div className="pt-2 border-t">
                      <p className="text-xs text-slate-500 mb-3">
                        Next session: {formatDate(group.next_session)}
                      </p>
                      <Button 
                        className="w-full"
                        onClick={() => {
                          setSelectedGroup(group);
                          setShowRegistrationDialog(true);
                        }}
                        disabled={!group.open_enrollment}
                        data-testid={`register-${group.id}`}
                      >
                        {group.open_enrollment ? 'Register for This Group' : 'Join Waitlist'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Bereavement Program Tab */}
          <TabsContent value="program" className="space-y-6">
            {bereavementProgram && (
              <>
                <Card className="bg-gradient-to-r from-teal-50 to-white border-teal-200">
                  <CardHeader>
                    <CardTitle className="text-2xl text-teal-800">{bereavementProgram.program_name}</CardTitle>
                    <CardDescription className="text-base">{bereavementProgram.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4 text-sm text-teal-700">
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4" />
                        <span>{bereavementProgram.contact.phone}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MessageCircle className="h-4 w-4" />
                        <span>{bereavementProgram.contact.email}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <h3 className="text-xl font-semibold text-slate-800">Program Services</h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {bereavementProgram.services.map((service, index) => (
                    <Card key={index} className="hover:shadow-md transition-shadow">
                      <CardHeader className="pb-2">
                        <Badge variant="secondary" className="w-fit mb-2">{service.timing}</Badge>
                        <CardTitle className="text-base">{service.name}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-slate-600">{service.description}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            )}
          </TabsContent>

          {/* Counseling Tab */}
          <TabsContent value="counseling" className="space-y-6">
            {counselingResources && (
              <>
                {/* Internal Services */}
                <Card className="border-l-4 border-l-teal-500">
                  <CardHeader>
                    <CardTitle className="text-xl">Goodwin Cares Bereavement Services</CardTitle>
                    <CardDescription>{counselingResources.internal_services.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-3 gap-4">
                      {counselingResources.internal_services.services.map((service, index) => (
                        <div key={index} className="p-4 bg-slate-50 rounded-lg">
                          <h4 className="font-semibold text-slate-800 mb-2">{service.name}</h4>
                          <p className="text-sm text-slate-600 mb-2">{service.description}</p>
                          <Badge variant="outline">{service.sessions_included}</Badge>
                          <p className="text-xs text-teal-600 mt-2">{service.how_to_access}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Crisis Resources */}
                <Card className="border-l-4 border-l-red-500">
                  <CardHeader>
                    <CardTitle className="text-xl flex items-center gap-2">
                      <AlertCircle className="h-5 w-5 text-red-500" />
                      Crisis Resources
                    </CardTitle>
                    <CardDescription>If you're in crisis, please reach out for immediate support</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-3 gap-4">
                      {counselingResources.crisis_resources.map((resource, index) => (
                        <div key={index} className="p-4 bg-red-50 rounded-lg border border-red-200">
                          <h4 className="font-semibold text-red-800 mb-1">{resource.name}</h4>
                          <p className="text-2xl font-bold text-red-600 mb-2">
                            {resource.phone || resource.contact}
                          </p>
                          <p className="text-sm text-red-700">{resource.description}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* External Resources */}
                <div>
                  <h3 className="text-xl font-semibold text-slate-800 mb-4">Additional Resources</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {counselingResources.external_resources.map((resource, index) => (
                      <Card key={index} className="hover:shadow-md transition-shadow">
                        <CardContent className="pt-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="font-semibold text-slate-800">{resource.name}</h4>
                              <p className="text-sm text-slate-600 mb-2">{resource.description}</p>
                              <a 
                                href={`https://${resource.website}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-teal-600 hover:text-teal-700 text-sm flex items-center gap-1"
                              >
                                {resource.website}
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          {/* Memorials Tab */}
          <TabsContent value="memorials" className="space-y-6">
            {memorialIdeas && (
              <>
                <div className="text-center mb-8">
                  <Flame className="h-12 w-12 text-amber-500 mx-auto mb-4" />
                  <h2 className="text-2xl font-semibold text-slate-800">Creating Meaningful Memorials</h2>
                  <p className="text-slate-600 max-w-2xl mx-auto mt-2">
                    Honor your loved one's memory in ways that bring comfort and keep their spirit alive
                  </p>
                </div>

                {/* Memorial Ideas */}
                <div className="grid md:grid-cols-2 gap-6">
                  {memorialIdeas.memorial_ideas.map((category, index) => (
                    <Card key={index}>
                      <CardHeader>
                        <CardTitle className="text-lg">{category.category}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-2">
                          {category.ideas.map((idea, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                              <Heart className="h-4 w-4 text-teal-500 mt-0.5 flex-shrink-0" />
                              <span>{idea}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Legacy Projects */}
                <div>
                  <h3 className="text-xl font-semibold text-slate-800 mb-4">Legacy Project Ideas</h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {memorialIdeas.legacy_project_ideas.map((project, index) => (
                      <Card key={index} className="hover:shadow-md transition-shadow">
                        <CardContent className="pt-4">
                          <h4 className="font-semibold text-slate-800 mb-2">{project.name}</h4>
                          <p className="text-sm text-slate-600">{project.description}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Support Request Dialog */}
      <Dialog open={showSupportRequestDialog} onOpenChange={setShowSupportRequestDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Request Grief Support</DialogTitle>
            <DialogDescription>
              Our bereavement team will contact you within 24 hours
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="support-name">Your Name</Label>
              <Input
                id="support-name"
                value={supportRequest.name}
                onChange={(e) => setSupportRequest({...supportRequest, name: e.target.value})}
                data-testid="support-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="support-phone">Phone</Label>
                <Input
                  id="support-phone"
                  value={supportRequest.phone}
                  onChange={(e) => setSupportRequest({...supportRequest, phone: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="support-email">Email</Label>
                <Input
                  id="support-email"
                  type="email"
                  value={supportRequest.email}
                  onChange={(e) => setSupportRequest({...supportRequest, email: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="support-type">Type of Support Needed</Label>
              <Select 
                value={supportRequest.support_type} 
                onValueChange={(value) => setSupportRequest({...supportRequest, support_type: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="counseling">Individual Counseling</SelectItem>
                  <SelectItem value="support_group">Support Group</SelectItem>
                  <SelectItem value="resources">Grief Resources</SelectItem>
                  <SelectItem value="crisis">Crisis Support (Urgent)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="support-message">Message (Optional)</Label>
              <Textarea
                id="support-message"
                value={supportRequest.message}
                onChange={(e) => setSupportRequest({...supportRequest, message: e.target.value})}
                placeholder="Tell us how we can help..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSupportRequestDialog(false)}>Cancel</Button>
            <Button onClick={handleSupportRequest} data-testid="submit-support-request">
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Group Registration Dialog */}
      <Dialog open={showRegistrationDialog} onOpenChange={setShowRegistrationDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Register for Support Group</DialogTitle>
            <DialogDescription>
              {selectedGroup?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="reg-name">Your Name</Label>
              <Input
                id="reg-name"
                value={registrationData.name}
                onChange={(e) => setRegistrationData({...registrationData, name: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="reg-email">Email</Label>
                <Input
                  id="reg-email"
                  type="email"
                  value={registrationData.email}
                  onChange={(e) => setRegistrationData({...registrationData, email: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="reg-phone">Phone</Label>
                <Input
                  id="reg-phone"
                  value={registrationData.phone}
                  onChange={(e) => setRegistrationData({...registrationData, phone: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="reg-relationship">Relationship to Deceased</Label>
              <Select 
                value={registrationData.relationship} 
                onValueChange={(value) => setRegistrationData({...registrationData, relationship: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select relationship" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="spouse">Spouse/Partner</SelectItem>
                  <SelectItem value="parent">Parent</SelectItem>
                  <SelectItem value="child">Child</SelectItem>
                  <SelectItem value="sibling">Sibling</SelectItem>
                  <SelectItem value="grandparent">Grandparent</SelectItem>
                  <SelectItem value="friend">Friend</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="reg-loss-date">Approximate Date of Loss</Label>
              <Input
                id="reg-loss-date"
                type="date"
                value={registrationData.loss_date}
                onChange={(e) => setRegistrationData({...registrationData, loss_date: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="reg-needs">Special Needs or Requests</Label>
              <Textarea
                id="reg-needs"
                value={registrationData.special_needs}
                onChange={(e) => setRegistrationData({...registrationData, special_needs: e.target.value})}
                placeholder="Any accommodations or specific needs..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRegistrationDialog(false)}>Cancel</Button>
            <Button onClick={handleGroupRegistration} data-testid="submit-registration">
              Complete Registration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GriefSupport;
